import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { enums, route } from "../../../shim/jsbridge";
import { isEmpty } from "../util";

import { RenderWhen } from "../../../components";
import AllRowsSelector from "../../../components/AllRowsSelector";
import GridToolbarLight from "../../../components/GridToolbarLight";
import PillSwitch from "../../../components/PillSwitch";
import TextInputFilter from "../../../components/filter/TextInputFilter";
import { useSelectRowsContext } from "../../../hooks";
import LoadingAndErrorHandling from "../../commons/LoadingAndErrorHandling";
import NavBreadcrumb from "../components/NavBreadcrumb";
import ExtractedFilesClassificationFilter from "../components/extractedfilestable/ExtractedFilesClassificationFilter";
import { ExtractedFilesFileFormatFilter } from "../components/extractedfilestable/ExtractedFilesFileFormatFilter";
import ExtractedFilesTable from "../components/extractedfilestable/ExtractedFilesTable";
import ExtractedFilesTableExport from "../components/extractedfilestable/ExtractedFilesTableExport";
import { ExtractedFilesThreatNameFilter } from "../components/extractedfilestable/ExtractedFilesThreatNameFilter";
import useExtractedFilesFilterOptions from "../hooks/useExtractedFilesFilterOptions";
import useGetExtractedFiles from "../hooks/useGetExtractedFiles";
import useGetRelationshipPaths from "../hooks/useGetRelationshipPaths";

const FILTER_ALL = "";
const SAMPLE_RELATIONSHIP_QUERY_PARAM = "r";
const SAMPLE_RELATIONSHIP_PATH_QUERY_PARAM = "path";
const THREAT_STATUS_QUERY_PARAM = "threat_status";
const VIEW_TYPE_QUERY_PARAM = "view_type";

export const VIEW_TYPES = {
  DIR: { value: "DIR", text: "Breadcrumb view", label: "Breadcrumb view" },
  FLAT: { value: "FLAT", text: "Flat view", label: "Flat view" }
};

const ExtractedFilesContainer = ({
  sampleSourceHash,
  sampleSourceFileName
}) => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const { hash: sampleHash, sourceId } = useParams();
  const searchParams = new URLSearchParams(search);

  const [samplePathState, setSamplePathState] = useState(
    searchParams.get(SAMPLE_RELATIONSHIP_PATH_QUERY_PARAM) ?? "/"
  );

  const threatStatusQueryParam = searchParams.get(THREAT_STATUS_QUERY_PARAM);
  const initialClassification = threatStatusQueryParam
    ? Number(threatStatusQueryParam)
    : FILTER_ALL;
  const viewTypeSearchParam = searchParams.get(VIEW_TYPE_QUERY_PARAM);
  const initialViewType =
    VIEW_TYPES[viewTypeSearchParam?.toUpperCase()] ?? VIEW_TYPES.DIR;

  const { api, isSelectAll, selected } = useSelectRowsContext();

  const [currentPage, setCurrentPage] = useState(1);
  const [selectedFilter, setSelectedFilter] = useState(initialClassification);

  const [viewType, setViewType] = useState(initialViewType);
  const [filterThreatName, setFilterThreatName] = useState(FILTER_ALL);
  const [filterFileName, setFilterFileName] = useState(FILTER_ALL);
  const [filterFileFormat, setFilterFileFormat] = useState(FILTER_ALL);

  const sampleRelationshipID =
    searchParams.get(SAMPLE_RELATIONSHIP_QUERY_PARAM) ?? 0;

  const breadcrumbQP = {
    r: sampleRelationshipID,
    hashValue: sampleSourceHash
  };

  const { data: extractedFilesFilterOptions } = useExtractedFilesFilterOptions(
    [sampleSourceHash],
    sampleRelationshipID
  );
  const { file_formats: fileFormatOptions, threat_names: threatNameOptions } =
    extractedFilesFilterOptions ?? {};

  const { paths } = useGetRelationshipPaths(sampleSourceHash, breadcrumbQP);

  // search is from useLocation() — read-only, never mutated here, no render loop risk.
  useEffect(() => {
    setSamplePathState(
      new URLSearchParams(search).get(SAMPLE_RELATIONSHIP_PATH_QUERY_PARAM) ??
        "/"
    );
    setCurrentPage(1);
  }, [search]);

  const resetFlatViewFiltersAndPaging = () => {
    setSamplePathState("/");
    setCurrentPage(1);
    setFilterThreatName(FILTER_ALL);
    setFilterFileName(FILTER_ALL);
    setFilterFileFormat(FILTER_ALL);
  };

  const extractedFilesQP = {
    threat_name: filterThreatName,
    filename: filterFileName,
    file_format: filterFileFormat,
    view_type: viewType?.value,
    relationship: sampleRelationshipID || 0,
    path: samplePathState || "/",
    preview: "",
    threat_status: selectedFilter ?? "",
    hashValue: sampleSourceHash,
    page: currentPage || 1,
    page_size: 20,
    skip: 0
  };

  const { data, isLoading, error, refetch } = useGetExtractedFiles(
    sampleSourceHash,
    extractedFilesQP
  );

  const { results, count } = data || {
    count: 0,
    results: []
  };

  const handleExtractedFilesExport = downloadUrl => {
    window.open(downloadUrl, "_blank");
  };

  const toolbarItems = [
    ...(viewType === VIEW_TYPES.FLAT
      ? [
          {
            component: index => (
              <TextInputFilter
                key={index}
                onChange={value => {
                  setCurrentPage(1);
                  setFilterFileName(value);
                }}
                placeholder={"Filter by File Name or SHA1"}
                field={"filename"}
                style={{ minWidth: "140px", flexGrow: 1, maxWidth: "340px" }}
              />
            )
          }
        ]
      : []),
    {
      component: index => (
        <ExtractedFilesClassificationFilter
          key={index}
          filter={selectedFilter}
          filterValues={enums.threat_status}
          withAllFilter
          onChange={({ value }) => {
            setCurrentPage(1);
            setSelectedFilter(value);
          }}
        />
      )
    },
    ...(viewType === VIEW_TYPES.FLAT
      ? [
          {
            component: index => (
              <ExtractedFilesThreatNameFilter
                key={index}
                index={index}
                onChange={({ value }) => {
                  setCurrentPage(1);
                  setFilterThreatName(value);
                }}
                active={filterThreatName}
                menuItems={threatNameOptions?.map(option => ({
                  value: option.id,
                  label: option.display_value
                }))}
              />
            )
          },
          {
            component: index => (
              <ExtractedFilesFileFormatFilter
                key={index}
                index={index}
                onChange={({ value }) => {
                  setCurrentPage(1);
                  setFilterFileFormat(value);
                }}
                active={filterFileFormat}
                menuItems={fileFormatOptions?.map(option => ({
                  value: option,
                  label: option
                }))}
              />
            )
          }
        ]
      : []),
    {
      component: index => (
        <ExtractedFilesTableExport
          key={index}
          sampleHash={sampleSourceHash}
          sampleRelationshipId={sampleRelationshipID}
          threatStatus={selectedFilter}
          fileName={filterFileName}
          fileFormat={filterFileFormat}
          threatName={filterThreatName}
          samplePath={samplePathState}
          downloadExtractedFiles={handleExtractedFilesExport}
          sampleData={results}
          selectedSamples={selected}
        />
      )
    }
  ];

  const handleBreadcrumbItemClick = ({ value }) => {
    if (value.r.toString() === sampleRelationshipID.toString()) {
      setCurrentPage(1);
      setSamplePathState(value.path || "/");
    } else {
      navigate(
        `${route("samples_details_main", {
          hash_value: value.hashValue || sampleSourceHash,
          source_id: sourceId
        })}extracted-files/?r=${value.r}`
      );
    }
  };

  const handleClassificationChange = () => {
    // allow classification to be stored into solr
    setTimeout(() => refetch(), 2500);
  };

  const handleViewHexClick = ({ value }) => {
    const { sha1 } = value?.sample || {};
    navigate(
      `${route("samples_details_main", {
        hash_value: sha1,
        source_id: sourceId
      })}file-visualization/?r=${value.id}`
    );
  };

  const handleSelectAll = () => {
    api.selectAll(results, currentPage);
  };

  const handleDeselectAll = () => {
    api.clear();
  };

  return (
    <div id="extracted-files" className="rlbs tab-content">
      <div id="extracted-files-widget">
        <div
          id="extracted-files-section"
          className="samples-grid flex flex-column"
        >
          <div className="d-flex flex-row justify-content-between align-items-end">
            <NavBreadcrumb
              sourceFileName={sampleSourceFileName}
              relationshipPaths={paths}
              currentPath={samplePathState}
              sampleRelationshipID={sampleRelationshipID}
              sourceHash={sampleSourceHash}
              onItemClick={handleBreadcrumbItemClick}
            />

            <PillSwitch
              field="view_type"
              activeFilter={viewType?.value}
              items={Object.values(VIEW_TYPES)}
              handleFilterChange={newViewType => {
                api.clear();
                resetFlatViewFiltersAndPaging();
                setViewType(VIEW_TYPES[newViewType]);
              }}
            />
          </div>

          <LoadingAndErrorHandling error={error}>
            <GridToolbarLight
              disabled={isLoading}
              items={toolbarItems}
              className="mt-10 mb-5"
            />
            <RenderWhen condition={isSelectAll || !isEmpty(selected)}>
              <AllRowsSelector
                allRowsSelected={isSelectAll}
                totalResults={count}
                selected={Object.values(selected)}
                onSelectAllRowsChange={handleSelectAll}
                onDeSelectAllRowsChange={handleDeselectAll}
              />
            </RenderWhen>
            <ExtractedFilesTable
              key={`${samplePathState}_${viewType.value}`}
              results={results}
              count={count}
              isLoading={isLoading}
              page={currentPage}
              gridErrorMsg={error}
              onPageChange={pageData => setCurrentPage(pageData)}
              hashValue={sampleHash}
              sampleSourceId={sourceId}
              onDirectoryClick={({ value }) => {
                api.clear();
                setCurrentPage(1);
                setSamplePathState(value.path || "/");
              }}
              onClassificationChange={handleClassificationChange}
              onViewHex={handleViewHexClick}
              sampleSourceHash={sampleSourceHash}
              extractedFilesQP={extractedFilesQP}
              viewType={viewType.value}
            />
          </LoadingAndErrorHandling>
        </div>
      </div>
    </div>
  );
};

export default ExtractedFilesContainer;
